import { defineStore } from 'pinia';
import ApiProtocolError from '@/errors/ApiProtocolError';
import LivechatService from '@/services/livechat';
import i18n from '@/i18n';
import { useBotsStore } from '@/stores/bots';
import { resolveLegacyNamespace } from '@/stores/legacyBridge';

function prepareAlertFromError(error: any, message: string | null = null) {
    let errorMessage = message || error.message;
    if (error instanceof ApiProtocolError) {
        errorMessage = `${(error as any).code}: ${(i18n as any).global.t(`livechat.errorMsgs.${(error as any).code}`)}`;
    }
    return {
        message: errorMessage,
        type: 'error',
        scope: 'global',
    }
}

/** Livechat organization as returned by the backend (loose, backend-owned shape) */
interface LivechatOrganization {
    /** Organization UUID */
    uuid: string;
    /** Agents belonging to the organization */
    agents: any[];
    /** Agent groups of the organization */
    groups: any[];
    /** Files attached to the organization */
    files?: any[];
    /** Shortcodes ({ code, text }) of the organization */
    shortcodes?: any[];
    /** Conversation tags of the organization */
    tags?: any[];
    /** Other backend fields */
    [key: string]: any;
}

interface LivechatManagementState {
    /** Currently loaded livechat organization, null before init */
    organization: LivechatOrganization | null;
    /** True while init() is fetching the organization */
    isLoading: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/livechatManagement.ts` (namespace
 * `livechatManagement`). In-memory only — no `vuexPersist` option (matches legacy: this module was
 * not registered in either `vuexLocal`/`vuexSession` reducer in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Pinia forbids a getter sharing a name with a state property. Legacy had 2 such collisions,
 *   both pure passthroughs — renamed `<name>Getter`, state stays readable directly under its
 *   original name:
 *   - getter `organization` → `organizationGetter`
 *   - getter `isLoading` → `isLoadingGetter`
 * - `agents`/`groups` getters had no colliding state prop (they derive from `organization`) — kept
 *   as-is.
 * - Mutation `setOrganization` (no matching legacy action existed) → same-named action
 *   `setOrganization`.
 * - `init`'s legacy `dispatch('fetchOrganization', ...)` and every action's `commit('setOrganization', ...)`
 *   become plain `this.fetchOrganization(...)` / `this.setOrganization(...)` calls.
 * - Cross-namespace access, resolved via sibling composables/registry called INSIDE the relevant
 *   action body (never at module top level):
 *   - `commit('hubUi/addAlert', payload, { root: true })` → `resolveLegacyNamespace('hubUi')?.addAlert(payload)`.
 *     `hubUi` has not been converted to Pinia yet as of this module's conversion — resolved via the
 *     legacy bridge registry (same pattern `preferences.ts` used for the not-yet-converted `bots`
 *     store), and will start working for real the moment a later phase registers `hubUi` there.
 *   - `rootGetters['livechat/agent']` / `dispatch('livechat/init', ..., { root: true })` →
 *     `resolveLegacyNamespace('livechat')?.agent` / `resolveLegacyNamespace('livechat')?.init(...)`.
 *     `livechat` has not been converted to Pinia yet either — same legacy-bridge pattern.
 *   - `rootGetters['bots/currentBotId']` → `useBotsStore().currentBotId` (`bots` IS already
 *     converted; `currentBotId` was not renamed there, no collision).
 */
export const useLivechatManagementStore = defineStore('livechatManagement', {
    state: (): LivechatManagementState => ({
        organization: null,
        isLoading: false,
    }),
    getters: {
        /** Renamed from legacy getter `organization` (collided with the `organization` state prop). */
        organizationGetter(state: LivechatManagementState): LivechatOrganization | null {
            return state.organization;
        },
        agents(state: LivechatManagementState) {
            return state.organization?.agents;
        },
        groups(state: LivechatManagementState) {
            return state.organization?.groups;
        },
        /** Renamed from legacy getter `isLoading` (collided with the `isLoading` state prop). */
        isLoadingGetter(state: LivechatManagementState): boolean {
            return state.isLoading;
        },
    },
    actions: {
        async init(uniqueBotId: string) {
            this.isLoading = true;
            await this.fetchOrganization(uniqueBotId);
            this.isLoading = false;
        },
        async fetchOrganization(uniqueBotId: string) {
            const organization = await LivechatService.getOrganizatioByBotId(uniqueBotId);
            this.setOrganization(organization);
        },
        async removeAgentGroup({ agentUuid, groupUuid }: { agentUuid: string; groupUuid: string }) {
            try {
                const agent: any = await LivechatService.removeAgentGroup(agentUuid, groupUuid);
                const agents = this.organization!.agents;
                agents.find((a: any) => a.uuid === agentUuid).groups = [...agent.groups]
                this.setOrganization({
                    ...this.organization,
                    agents: [...agents]
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },

        async addAgentGroups({ agentUuid, groups }: { agentUuid: string; groups: string[] }) {
            try {
                const agent: any = await LivechatService.addAgentGroups(agentUuid, groups);
                const agents = this.organization!.agents;
                agents.find((a: any) => a.uuid === agentUuid).groups = [...agent.groups]
                this.setOrganization({
                    ...this.organization,
                    agents: [...agents]
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        /**
         *
         * @param {object} context
         * @param {object} payload - The payload object.
         * @param {string} payload.username - email of the agent.
         * @param {string[]} payload.groups - The UUIDs of the groups.
         */
        async createAndAddAgent({ username, groups }: { username: string; groups: string[] }) {
            try {
                const agent = await LivechatService.createAndAddAgent(username, this.organization!.uuid, groups);

                this.organization!.agents.push(agent);
                this.setOrganization({
                    ...this.organization
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        async updateAgent({ agent, routePath }: { agent: any; routePath?: string }) {
            try {
                const updatedAgent: any = await LivechatService.updateAgent(agent);
                const agents = this.organization!.agents;
                const currentAgent = agents.find((a: any) => a.uuid === agent.uuid);

                Object.keys(updatedAgent).forEach(key => {
                    currentAgent[key] = updatedAgent[key];
                });
                this.setOrganization({
                    ...this.organization,
                    agents: [...agents]
                } as LivechatOrganization);

                // Check if updated agent is current livechat agent and re-init livechat store
                const livechatAgent = resolveLegacyNamespace('livechat')?.agent;
                if (livechatAgent && livechatAgent?.uuid === agent.uuid) {
                    await resolveLegacyNamespace('livechat')?.init({
                        botId: useBotsStore().currentBotId,
                        routePath: routePath
                    });
                }
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        async deleteAgent(agentUuid: string) {
            try {
                await LivechatService.deleteAgent(agentUuid);

                this.setOrganization({
                    ...this.organization,
                    agents: this.organization!.agents.filter((a: any) => a.uuid !== agentUuid)
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        /**
         * Adds a new group to the organization.
         *
         * @param {object} context
         * @param {import('@/services/livechat').Group} newGroup
         */
        async createGroup(newGroup: any) {
            try {
                const organization: any = await LivechatService.addGroupToOrganization(this.organization!.uuid, newGroup);
                this.setOrganization({
                    ...this.organization,
                    groups: [...organization.groups]
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        /**
         * Updates an existing group.
         *
         * @param {object} context
         * @param {import('@/services/livechat').Group} group
         */
        async updateGroup(group: any) {
            try {
                const organization: any = await LivechatService.updateGroup(this.organization!.uuid, group);
                this.setOrganization({
                    ...this.organization,
                    groups: [...organization.groups]
                } as LivechatOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
                throw new Error("Failed to update group");
            }
        },

        /**
         * Deletes a group from the organization.
         *
         * @param {object} context
         * @param {import('@/services/livechat').Group} group
         */
        async deleteGroup(group: any) {
            try {
                const result: any = await LivechatService.deleteGroup(this.organization!.uuid, group);

                // The API answers with HTTP 200 and `{error: '<forms using the group>'}` when the
                // group may not be deleted. A successful delete carries no `error` — it never had a
                // `success` flag either, so only `error` may be used to detect the failure case.
                if (result?.error) {
                    return { success: false, error: result.error };
                }

                const organization = result?.data ?? result;
                const groups = Array.isArray(organization?.groups)
                    ? [...organization.groups]
                    : (this.organization?.groups ?? []).filter((g: any) => g.uuid !== group.uuid);

                this.setOrganization({
                    ...this.organization,
                    groups
                } as LivechatOrganization);

                return { success: true, message: "Group deleted successfully" };
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
                throw new Error("Failed to delete group");
            }
        },
        async addOrganizationFile(file: {
            name: string;
            url: string;
            description?: string;
            extension?: string;
            mimeType?: string;
            size?: number | null;
            originalFilename?: string;
        }) {
            try {
                const result: any = await LivechatService.addFileToOrganization(this.organization!.uuid, file);
                this.setOrganization({
                    ...this.organization,
                    files: result.files || this.organization!.files
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to add file");
            }
        },

        async removeOrganizationFile(fileName: string) {
            try {
                const result: any = await LivechatService.removeFileFromOrganization(this.organization!.uuid, fileName);
                this.setOrganization({
                    ...this.organization,
                    files: result.files || this.organization!.files
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to remove file");
            }
        },

        async addOrganizationShortcode(shortcode: { code: string; text: string }) {
            // shortcode: { code: string, text: string }
            try {
                const result: any = await LivechatService.addShortcodeToOrganization(this.organization!.uuid, shortcode);
                this.setOrganization({
                    ...this.organization,
                    shortcodes: result.shortcodes || this.organization!.shortcodes
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to add shortcode");
            }
        },

        async updateOrganizationShortcode({ code, shortcode }: { code: string; shortcode: { code: string; text: string } }) {
            // shortcode: { code: string, text: string }
            try {
                const result: any = await LivechatService.updateShortcodeForOrganization(this.organization!.uuid, code, shortcode);
                this.setOrganization({
                    ...this.organization,
                    shortcodes: result.shortcodes || this.organization!.shortcodes
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to update shortcode");
            }
        },

        async removeOrganizationShortcode(code: string) {
            try {
                const result: any = await LivechatService.removeShortcodeFromOrganization(this.organization!.uuid, code);
                this.setOrganization({
                    ...this.organization,
                    shortcodes: result.shortcodes || this.organization!.shortcodes
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to remove shortcode");
            }
        },
        async createTag(newTag: any) {
            try {
                const organization: any = await LivechatService.addOrganizationTag(this.organization!.uuid, newTag);
                this.setOrganization({
                    ...this.organization,
                    tags: organization.tags
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to add tag");
            }
        },

        async updateTag({ tagName, tagData }: { tagName: string; tagData: any }) {
            try {
                const organization: any = await LivechatService.updateOrganizationTag(this.organization!.uuid, tagName, tagData);
                this.setOrganization({
                    ...this.organization,
                    tags: organization.tags
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to update tag");
            }
        },

        async deleteTag(tagTitle: string) {
            try {
                const organization: any = await LivechatService.deleteOrganizationTag(this.organization!.uuid, tagTitle);
                this.setOrganization({
                    ...this.organization,
                    tags: organization.tags
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to delete tag");
            }
        },

        async updateOrganization({ uuid, organizationData }: { uuid: string; organizationData: any }) {
            try {
                const updatedOrganization = await LivechatService.updateOrganizationLimited(uuid, organizationData);

                this.setOrganization(updatedOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },
        async updateOrganizationAdmin({ uuid, organizationData }: { uuid: string; organizationData: any }) {
            try {
                const updatedOrganization = await LivechatService.updateOrganizationAdmin(uuid, organizationData);

                this.setOrganization(updatedOrganization);
            } catch (e) {
                resolveLegacyNamespace('hubUi')?.addAlert(prepareAlertFromError(e));
                console.error(e);
            }
        },

        async updateOrganizationFileDescription({ fileName, description }: { fileName: string; description: string }) {
            try {
                const result: any = await LivechatService.updateFileDescription(this.organization!.uuid, fileName, description);
                this.setOrganization({
                    ...this.organization,
                    files: result.files || this.organization!.files
                } as LivechatOrganization);
            } catch (e) {
                console.error(e);
                throw new Error("Failed to update file description");
            }
        },
        /**
         * Converted from legacy mutation `setOrganization` (no matching legacy action existed).
         * Merges into the existing organization (if any), matching legacy vuex behavior exactly.
         */
        setOrganization(organization: LivechatOrganization) {
            if (this.organization) {
                this.organization = {
                    ...this.organization,
                    ...organization
                }
            } else {
                this.organization = organization;
            }
        },
    },
});
